import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { useTimeIntervalStore } from '../../../stores/appTimeIntervalStore';
import { resolveWastePercentage } from '../../../utils/wasteUtils';
import { eur } from '../../orderhistory/utils';
import { rangeFromTimeControls } from '../../../utils/reports/shared';
import { ReportBarChart, CHART_RED } from '../../pages/reports/hub/common';
import { computeUtilizationReport } from '../../../utils/reports/utilizationReport';
import { cardClass, primaryText, secondaryText, semiBoldText, boldText, xxLargeTextSize } from '../../../styling/stylingHelper';
import { cn } from '../../../lib/utils';

interface CostPercentWasteCardProps {
  side: 'all' | 'food' | 'drinks';
}

/**
 * Compact waste card for the Cost Percent page: total potential waste € for the
 * selected period plus the top-5 products by waste. Reuses the same
 * computeUtilizationReport engine as the full waste report; "See all" deep-links
 * to /costpercent/waste for the detailed graph + table.
 */
export const CostPercentWasteCard: React.FC<CostPercentWasteCardProps> = ({ side }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const orders = useOrderHistory(s => s.orderHistoryList);
  const revenue = useRevenueHistory(s => s.revenueHistoryList);
  const costPriceList = useCostPriceStore(s => s.costPriceList);
  const subrecipesById = useSubrecipesStore(s => s.subrecipesById);
  const restaurantId = useAppUserDefaultStore(s => s.restaurant?.id) ?? '';
  const reportSettings = useRestaurantStore(s => (restaurantId ? s.reportSettings[restaurantId] : undefined));
  const defaultWastePercentage = useMemo(() => resolveWastePercentage(reportSettings?.wastePercentage), [reportSettings]);

  const view = useTimeIntervalStore(s => s.view);
  const startDate = useTimeIntervalStore(s => s.startDate);
  const endDate = useTimeIntervalStore(s => s.endDate);

  const data = useMemo(() =>
    computeUtilizationReport({
      orders, revenue, costPriceList, subrecipesById, defaultWastePercentage,
      currentRestaurantId: restaurantId,
      range: rangeFromTimeControls(view, startDate, endDate),
    }),
    [orders, revenue, costPriceList, subrecipesById, defaultWastePercentage, restaurantId, view, startDate, endDate]
  );

  const rows = useMemo(() =>
    data.rows
      .filter(r => side === 'all' || r.side === side)
      .filter(r => r.wasteEur !== null && r.wasteEur > 0),
    [data.rows, side]
  );

  const totalWaste = useMemo(() => rows.reduce((s, r) => s + (r.wasteEur as number), 0), [rows]);
  const chartData = useMemo(() =>
    [...rows].sort((a, b) => (b.wasteEur as number) - (a.wasteEur as number)).slice(0, 5).map(r => ({ label: r.name, value: r.wasteEur as number })),
    [rows]
  );

  return (
    <div className={cn(cardClass, 'p-5 flex flex-col h-full')}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className={cn(primaryText, semiBoldText, 'text-[18px]')}>{t('costPercent.waste.cardTitle')}</h2>
          <div className="mt-2 flex items-baseline gap-2">
            <span className={cn(primaryText, xxLargeTextSize, boldText)}>{eur(totalWaste)}</span>
            <span className={cn(secondaryText, 'text-[13px]')}>{t('costPercent.waste.totalLabel')}</span>
          </div>
        </div>
        <button
          onClick={() => navigate('/costpercent/waste')}
          className="flex items-center gap-1 text-[14px] font-semibold text-textGreen dark:text-textGreen-dark hover:underline"
        >
          {t('costPercent.waste.seeAll')}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      {chartData.length > 0 ? (
        <ReportBarChart data={chartData} valueFormatter={eur} defaultColor={CHART_RED} />
      ) : (
        <p className={cn(secondaryText, 'text-center py-8 text-[14px]')}>{t('costPercent.waste.empty')}</p>
      )}
    </div>
  );
};
